"use client";

import React from "react";
import { PhoneCall, Database, Mic, ChartBar, TrendingUp, Target, Settings, ChartPie, Play, Pause, Pen, Trash2, Users, PhoneForwarded, Activity } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const benefits = [{
  icon: TrendingUp,
  text: "Aumentar la productividad al reducir tiempos muertos."
}, {
  icon: Target,
  text: "Enfocar esfuerzos en llamadas efectivas."
}, {
  icon: Settings,
  text: "Automatizar campañas de ventas, cobranza o atención."
}, {
  icon: ChartPie,
  text: "Obtener métricas en tiempo real para mejorar resultados."
}];
const campaigns = [{
  id: 1,
  desc: "Campaña Ventas Q3",
  status: "Activa",
  type: "Semi AUTO",
  contacts: 14002,
  totalCalls: 12,
  successCalls: 1,
  ok: "0%",
  fails: "3%",
  pending: "96%"
}, {
  id: 2,
  desc: "Cobranza Preventiva",
  status: "Activa",
  type: "Semi AUTO",
  contacts: 15493,
  totalCalls: 69,
  successCalls: 2,
  ok: "0%",
  fails: "6%",
  pending: "93%"
}, {
  id: 3,
  desc: "Encuesta Satisfacción",
  status: "Activa",
  type: "Semi AUTO",
  contacts: 20031,
  totalCalls: 0,
  successCalls: 0,
  ok: "0%",
  fails: "7%",
  pending: "91%"
}, {
  id: 4,
  desc: "Renovación Pólizas",
  status: "Inactiva",
  type: "Semi AUTO",
  contacts: 5000,
  totalCalls: 0,
  successCalls: 0,
  ok: "0%",
  fails: "0%",
  pending: "100%"
}, {
  id: 5,
  desc: "Prueba de números RD",
  status: "Inactiva",
  type: "Prueba",
  contacts: 4996,
  totalCalls: 0,
  successCalls: 0,
  ok: "0%",
  fails: "30%",
  pending: "69%"
}];
export default function PredictiveDialerAutomation() {
  return <section id="predictivedialerautomation" className="bg-background py-10 md:py-14">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <h2 className="font-default text-foreground mb-4 text-3xl font-bold tracking-tight md:text-4xl">
            Automatización y Gestión de Campañas
          </h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
            Optimice el rendimiento de su centro de contacto con herramientas
            avanzadas de marcación predictiva y análisis en tiempo real.
          </p>
        </div>

        <Tabs defaultValue="marcador" className="w-full">
          <div className="mb-8 flex justify-center overflow-x-auto pb-2">
            <TabsList className="h-auto flex-wrap justify-center gap-2 bg-transparent p-0">
              <TabsTrigger value="marcador" className="data-[state=active]:border-primary data-[state=active]:text-primary flex items-center gap-2 border-b-2 border-transparent bg-transparent px-4 py-3 text-base data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                <PhoneCall className="size-5" />
                Marcador Predictivo
              </TabsTrigger>
              <TabsTrigger value="crm" className="data-[state=active]:border-primary data-[state=active]:text-primary flex items-center gap-2 border-b-2 border-transparent bg-transparent px-4 py-3 text-base data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                <Database className="size-5" />
                Integración con CRM
              </TabsTrigger>
              <TabsTrigger value="grabacion" className="data-[state=active]:border-primary data-[state=active]:text-primary flex items-center gap-2 border-b-2 border-transparent bg-transparent px-4 py-3 text-base data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                <Mic className="size-5" />
                Grabación y Monitoreo
              </TabsTrigger>
              <TabsTrigger value="analitica" className="data-[state=active]:border-primary data-[state=active]:text-primary flex items-center gap-2 border-b-2 border-transparent bg-transparent px-4 py-3 text-base data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                <ChartBar className="size-5" />
                Analítica y Reportes
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="marcador" className="mt-0 outline-none">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
              {}
              <div className="flex flex-col justify-center lg:col-span-5">
                <div className="prose prose-gray dark:prose-invert max-w-none">
                  <p className="text-muted-foreground mb-6 text-base leading-relaxed md:text-lg">
                    Un <strong>marcador de llamadas</strong> es una herramienta
                    automatizada que forma parte de una central telefónica,
                    diseñada para optimizar el proceso de contacto con clientes.
                    Este sistema realiza llamadas de forma automática desde una
                    base de datos, conectando a los agentes solo cuando la
                    llamada es respondida.
                  </p>

                  <p className="text-foreground mb-4 font-medium">
                    Con un marcador inteligente, tu equipo puede:
                  </p>

                  <ul className="mb-8 space-y-4">
                    {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                        <div className="bg-primary/10 text-primary mt-1 flex shrink-0 items-center justify-center rounded-md p-1.5">
                          <benefit.icon className="size-4" />
                        </div>
                        <span className="text-muted-foreground text-base">
                          {benefit.text}
                        </span>
                      </li>)}
                  </ul>

                  <p className="text-muted-foreground text-base leading-relaxed">
                    Ya sea en un <strong>call center</strong>, un equipo de
                    ventas o atención al cliente, el marcador de llamadas
                    transforma la forma en que te comunicas, haciendo cada
                    llamada más rápida, eficiente y rentable.
                  </p>
                </div>
              </div>

              {}
              <div className="lg:col-span-7">
                <Card className="border-border shadow-lg">
                  <CardHeader className="bg-muted/30 border-b pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">
                          Monitor de Campañas
                        </CardTitle>
                        <CardDescription>
                          Estado en tiempo real de marcación automática
                        </CardDescription>
                      </div>
                      <Activity className="text-primary size-5 animate-pulse" />
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow>
                          <TableHead className="w-[150px]">Descripción</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead className="text-right">Contactos</TableHead>
                          <TableHead className="text-center">
                            Llamadas (Tot/Éxito)
                          </TableHead>
                          <TableHead className="text-right">OK</TableHead>
                          <TableHead className="text-right">Fallos</TableHead>
                          <TableHead className="text-right">Pendiente</TableHead>
                          <TableHead className="text-center">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaigns.map((campaign, i) => <TableRow key={campaign.id} data-index={i}>
                            <TableCell className="font-medium">
                              {campaign.desc}
                            </TableCell>
                            <TableCell>
                              <Badge variant={campaign.status === "Activa" ? "default" : "secondary"} className={campaign.status === "Activa" ? "bg-emerald-500 hover:bg-emerald-600" : ""}>
                                {campaign.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs">
                              {campaign.type}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {campaign.contacts.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-center tabular-nums">
                              {campaign.totalCalls} /{" "}
                              <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                {campaign.successCalls}
                              </span>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {campaign.ok}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {campaign.fails}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {campaign.pending}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="icon" className="size-7" title="Editar">
                                  <Pen className="text-muted-foreground size-3.5" />
                                </Button>
                                {campaign.status === "Activa" ? <Button variant="ghost" size="icon" className="size-7" title="Pausar">
                                    <Pause className="text-amber-500 size-3.5" />
                                  </Button> : <Button variant="ghost" size="icon" className="size-7" title="Iniciar">
                                    <Play className="text-emerald-500 size-3.5" />
                                  </Button>}
                                <Button variant="ghost" size="icon" className="size-7" title="Asignar Agentes">
                                  <Users className="text-primary size-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </CardContent>
                  <CardFooter className="bg-muted/30 flex flex-col items-start gap-4 border-t p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-muted-foreground text-xs">
                      La columna "Fallos" indica el % de contactos que
                      definitivamente no se pudieron contactar.
                    </div>
                    <div className="flex w-full flex-wrap gap-2 sm:w-auto">
                      <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                        <PhoneForwarded className="mr-2 size-3.5" />
                        Nueva Campaña AUTO
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                        Nueva Campaña Manual
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              </div>
            </div>
          </TabsContent>

          {}
          <TabsContent value="crm" className="mt-8 outline-none">
            <Card className="border-border shadow-sm">
              <CardContent className="flex flex-col items-center justify-center py-24 text-center">
                <Database className="text-muted-foreground mb-4 size-12 opacity-50" />
                <h3 className="font-default text-foreground mb-2 text-xl font-semibold">
                  Integración Perfecta con su CRM
                </h3>
                <p className="text-muted-foreground max-w-md">
                  Conecte su marcador predictivo directamente con Salesforce,
                  HubSpot, Zoho y más para mantener sus datos sincronizados en
                  tiempo real.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="grabacion" className="mt-8 outline-none">
            <Card className="border-border shadow-sm">
              <CardContent className="flex flex-col items-center justify-center py-24 text-center">
                <Mic className="text-muted-foreground mb-4 size-12 opacity-50" />
                <h3 className="font-default text-foreground mb-2 text-xl font-semibold">
                  Grabación y Monitoreo de Calidad
                </h3>
                <p className="text-muted-foreground max-w-md">
                  Asegure el cumplimiento y mejore la atención al cliente con
                  grabación automática de llamadas y herramientas de monitoreo en
                  vivo para supervisores.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analitica" className="mt-8 outline-none">
            <Card className="border-border shadow-sm">
              <CardContent className="flex flex-col items-center justify-center py-24 text-center">
                <ChartBar className="text-muted-foreground mb-4 size-12 opacity-50" />
                <h3 className="font-default text-foreground mb-2 text-xl font-semibold">
                  Analítica Avanzada
                </h3>
                <p className="text-muted-foreground max-w-md">
                  Tome decisiones basadas en datos con paneles personalizables
                  que muestran el rendimiento de los agentes, tasas de conversión
                  y tiempos de llamada.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}